import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Layers3,
  Scale,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const stages = [
  "Identificação",
  "Evidências",
  "Hipóteses",
  "Metodologia",
  "Manifestações",
  "Encerramento",
  "Word",
];

const benefits = [
  {
    icon: ClipboardCheck,
    title: "Completude antes da redação",
    text: "O caso passa por etapas guiadas para reduzir lacunas, saltos de raciocínio e esquecimentos que aparecem tarde demais.",
  },
  {
    icon: Scale,
    title: "Raciocínio técnico defendível",
    text: "Separe fato, hipótese, inferência, limitação e conclusão antes de transformar análise em documento.",
  },
  {
    icon: FileText,
    title: "Exportável para Word",
    text: "Ao final, o material estruturado vira uma base editável para continuar o refinamento profissional do laudo.",
  },
];

const plans = [
  {
    name: "Starter",
    tag: "Para começar",
    description: "Para profissionais que elaboram laudos próprios e querem um fluxo mais seguro.",
    features: ["Casos guiados", "Exportação para Word", "Checklist de lacunas", "Uso individual"],
    checkoutUrl: "https://pay.kiwify.com.br/Fe5s5eU",
  },
  {
    name: "Profissional",
    tag: "Mais escolhido",
    description: "Para quem produz laudos com frequência e precisa ganhar consistência sem abrir mão do critério técnico.",
    features: ["Tudo do Starter", "Organização avançada do caso", "Campos de metodologia e limitações", "Prioridade no relançamento"],
    checkoutUrl: "https://pay.kiwify.com.br/mCZC0Hb",
    highlighted: true,
  },
  {
    name: "Empresarial",
    tag: "Para equipes",
    description: "Para pequenas equipes técnicas que precisam padronizar a forma de pensar antes de padronizar o documento.",
    features: ["Fluxo para múltiplos usuários", "Padronização de entrega", "Base para revisão interna", "Condições sob consulta"],
    checkoutUrl: "https://pay.kiwify.com.br/qR2HaUb",
  },
];

const proofCards = [
  "A competência continua sendo sua.",
  "A estrutura é do Perytus.",
  "Antes do laudo, vem o raciocínio.",
];

const primaryCtaClasses =
  "group inline-flex h-14 w-full items-center justify-center gap-3 rounded-sm bg-[#103F46] px-7 text-center text-base font-semibold text-[#F8F4EA] shadow-[0_18px_44px_rgba(16,63,70,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#0B3036] hover:shadow-[0_22px_54px_rgba(16,63,70,0.28)] sm:w-auto";

const LandingPage = () => {
  return (
    <main className="min-h-screen overflow-hidden bg-[#F4F0E8] text-[#141817]">
      <section className="relative border-b border-[#1E3A3A]/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_10%,rgba(15,76,92,0.20),transparent_34%),linear-gradient(135deg,rgba(224,184,107,0.24),transparent_28%)]" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#F4F0E8] to-transparent" />

        <nav className="relative mx-auto flex max-w-7xl items-center justify-between px-5 py-6 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#103F46] text-[#F4F0E8] shadow-[0_12px_30px_rgba(16,63,70,0.22)]">
              <Layers3 size={20} />
            </div>
            <div>
              <p className="font-serif text-2xl leading-none tracking-tight">PERYTUS</p>
              <p className="text-xs uppercase tracking-[0.24em] text-[#5E6866]">Raciocínio técnico guiado</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden text-sm font-medium text-[#475150] hover:text-[#103F46] sm:inline">
              Entrar
            </Link>
            <a
              href="#acesso"
              className="inline-flex h-11 items-center justify-center rounded-sm bg-[#103F46] px-5 text-sm font-semibold text-[#F8F4EA] transition-colors hover:bg-[#0B3036]"
            >
              Acessar Perytus
            </a>
          </div>
        </nav>

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-10 md:grid-cols-[1fr_0.86fr] md:px-8 md:pb-28 md:pt-16">
          <div>
            <div className="mb-8 inline-flex items-center gap-2 border border-[#C9A463]/40 bg-[#FFF9ED]/70 px-3 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#755D2D]">
              <Sparkles size={14} />
              Relançamento
            </div>
            <h1 className="max-w-4xl font-serif text-5xl leading-[0.96] tracking-tight text-[#111514] md:text-7xl">
              Antes do laudo, vem o raciocínio.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#4A5553] md:text-xl">
              O Perytus guia profissionais de engenharia e arquitetura por um fluxo completo para estruturar laudos técnicos com método, etapas verificáveis, requisitos das normas técnicas pertinentes e exportação para Word.
            </p>
            <div className="mt-9">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <a href="#acesso" className={primaryCtaClasses}>
                  <span>Acessar Perytus agora</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F8F4EA]/12 transition-transform group-hover:translate-x-1">
                    <ArrowRight size={18} />
                  </span>
                </a>
                <a
                  href="#fluxo"
                  className="inline-flex h-14 items-center justify-center whitespace-nowrap rounded-sm border border-[#103F46]/25 bg-white/45 px-7 text-base font-semibold text-[#103F46] transition-colors hover:bg-white/75"
                >
                  Ver como funciona
                </a>
              </div>
              <p className="mt-3 text-sm font-medium text-[#56615F]">
                Comece a elaborar laudos de altíssimo nível com um fluxo completo e guiado.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -left-8 -top-8 h-32 w-32 border border-[#C9A463]/50" />
            <div className="relative border border-[#113B41]/15 bg-[#FBF7EF]/86 p-4 shadow-[0_24px_80px_rgba(19,32,34,0.16)] backdrop-blur">
              <div className="border border-[#113B41]/10 bg-[#0B1213] p-5 text-[#F8F4EA]">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-[#D5B46D]">Caso em andamento</p>
                    <p className="mt-1 font-serif text-2xl">Laudo técnico</p>
                  </div>
                  <ShieldCheck className="text-[#D5B46D]" size={26} />
                </div>
                <div className="space-y-3">
                  {stages.map((stage, index) => (
                    <div key={stage} className="flex items-center gap-3">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs ${index < 5 ? "bg-[#D5B46D] text-[#121716]" : "bg-white/10 text-white/70"}`}>
                        {index < 5 ? <CheckCircle2 size={16} /> : index + 1}
                      </div>
                      <div className="h-px flex-1 bg-white/10" />
                      <span className="min-w-28 text-right text-sm text-white/82">{stage}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-7 bg-white/[0.06] p-4">
                  <p className="text-sm uppercase tracking-[0.20em] text-white/45">Ponto de verificação</p>
                  <p className="mt-2 text-lg leading-7 text-white/86">
                    Há conclusão, limitações, referências e lacunas metodológicas antes da exportação?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="fluxo" className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <div className="grid gap-10 md:grid-cols-[0.75fr_1fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#9A7A36]">O apelo é completude</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight md:text-5xl">Não pule a etapa que sustenta sua entrega.</h2>
            <p className="mt-5 max-w-md text-sm leading-7 text-[#56615F]">
              O fluxo ajuda a manter à vista os pontos que costumam comprometer um laudo: escopo, evidências, metodologia, limitações, hipóteses, conclusão e aderência técnica.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {benefits.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="border border-[#163F45]/12 bg-white/64 p-6 shadow-sm">
                  <Icon className="mb-8 text-[#103F46]" size={28} />
                  <h3 className="font-serif text-2xl leading-tight">{item.title}</h3>
                  <p className="mt-4 text-sm leading-6 text-[#56615F]">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#111716] px-5 py-20 text-[#F6F0E7] md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 md:grid-cols-3">
            {proofCards.map((item) => (
              <div key={item} className="border-l border-[#D5B46D] pl-5">
                <p className="font-serif text-3xl leading-tight">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="acesso" className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#9A7A36]">Acesso</p>
          <h2 className="mt-4 font-serif text-4xl leading-tight md:text-5xl">Escolha a versão que combina com sua entrega técnica.</h2>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {plans.map((plan) => (
            <article key={plan.name} className={`relative border p-7 ${plan.highlighted ? "border-[#103F46] bg-[#103F46] text-[#F8F4EA] shadow-[0_22px_64px_rgba(16,63,70,0.22)]" : "border-[#163F45]/12 bg-white/70 text-[#141817]"}`}>
              <p className={`text-xs font-bold uppercase tracking-[0.22em] ${plan.highlighted ? "text-[#D5B46D]" : "text-[#9A7A36]"}`}>{plan.tag}</p>
              <h3 className="mt-5 font-serif text-3xl">{plan.name}</h3>
              <p className={`mt-4 min-h-20 text-sm leading-6 ${plan.highlighted ? "text-white/78" : "text-[#56615F]"}`}>{plan.description}</p>
              <ul className="mt-8 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 size={17} className={plan.highlighted ? "mt-0.5 text-[#D5B46D]" : "mt-0.5 text-[#103F46]"} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href={plan.checkoutUrl}
                target="_blank"
                rel="noreferrer"
                className={`mt-8 inline-flex h-12 w-full items-center justify-center rounded-sm text-sm font-semibold transition-colors ${plan.highlighted ? "bg-[#F8F4EA] text-[#103F46] hover:bg-white" : "bg-[#103F46] text-[#F8F4EA] hover:bg-[#0B3036]"}`}
              >
                Comprar {plan.name}
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="px-5 pb-20 md:px-8">
        <div className="mx-auto max-w-7xl border border-[#163F45]/12 bg-[#FBF7EF] p-8 md:p-12">
          <div className="grid items-center gap-8 md:grid-cols-[1fr_auto]">
            <div>
              <h2 className="font-serif text-4xl leading-tight">Acesse Perytus elevando o nível de sua entrega.</h2>
              <p className="mt-4 max-w-2xl text-[#56615F]">
                Comece com um fluxo que ajuda a manter o caso inteiro à vista: requisitos técnicos, evidências, hipóteses, lacunas, conclusão e documento final.
              </p>
            </div>
            <a
              href="#acesso"
              className="inline-flex h-14 items-center justify-center rounded-sm bg-[#103F46] px-7 text-base font-semibold text-[#F8F4EA] transition-colors hover:bg-[#0B3036]"
            >
              Escolher acesso
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
